/** @file Barycentric weights of a point in one RenderBuffers triangle (index position t, t+1, t+2). */
import { RENDER_LAYOUT } from '@pose2d/geometry/renderBuffers';
import type { RenderBuffers } from '@pose2d/geometry/types';
import { readNum } from './readNum';

const W = RENDER_LAYOUT.VERTEX_WORDS;
const EPS = 1e-9;

/** Writes the weights of (x, y) for the triangle starting at index position t into out[0..2]; true when inside. */
export function triangleWeightsAt(buffers: RenderBuffers, t: number, x: number, y: number, out: Float64Array): boolean {
  const f = buffers.vertexF32;
  const a = readNum(buffers.indices, t) * W;
  const b = readNum(buffers.indices, t + 1) * W;
  const c = readNum(buffers.indices, t + 2) * W;
  const ax = readNum(f, a), ay = readNum(f, a + 1);
  const bx = readNum(f, b), by = readNum(f, b + 1);
  const cx = readNum(f, c), cy = readNum(f, c + 1);
  const det = (by - cy) * (ax - cx) + (cx - bx) * (ay - cy);
  if (det === 0) return false;
  const w0 = ((by - cy) * (x - cx) + (cx - bx) * (y - cy)) / det;
  const w1 = ((cy - ay) * (x - cx) + (ax - cx) * (y - cy)) / det;
  const w2 = 1 - w0 - w1;
  out[0] = w0;
  out[1] = w1;
  out[2] = w2;
  return w0 >= -EPS && w1 >= -EPS && w2 >= -EPS;
}
